/**
 * @fileoverview API Factory Module
 *
 * This module provides factory classes for creating and managing Axios instances.
 * It implements the singleton pattern for instance management and provides a clean
 * wrapper interface for HTTP operations.
 *
 * Key features:
 * - Singleton factory pattern (ApiService) - one instance per key
 * - Clean HTTP wrapper (ApiWrapper) with lazy initialization
 * - Automatic interceptor setup on instance creation
 * - Credentials handling for specific instances (orchestrator)
 *
 * @module services/api/factory
 */

import axios, { AxiosInstance, AxiosResponse, AxiosHeaderValue, AxiosRequestConfig } from 'axios';

import { ApiConfig, ApiMethods, INSTANCES_WITH_CREDENTIALS } from './config';
import { setupInterceptors, clearInterceptors } from './interceptors';
import { logger } from 'utils/logger';

// ============================================================================
// External Function Reference
// ============================================================================

/**
 * Function to update headers across all API instances
 * Set externally from instances.ts to avoid circular dependency
 * @private
 */
let updateHeadersFunction: ((token: string, orgUUID?: string, userPool?: string) => void) | null =
  null;

/**
 * Sets the function used to update headers across all instances
 *
 * This is called from instances.ts after defaultsHeadersAxios is defined,
 * avoiding circular dependency issues between factory and instances modules.
 *
 * @param fn - The function that updates headers across all API instances
 *
 * @example
 * // In instances.ts:
 * import { setUpdateHeadersFunction } from './factory';
 * setUpdateHeadersFunction(defaultsHeadersAxios);
 */
export const setUpdateHeadersFunction = (
  fn: (token: string, orgUUID?: string, userPool?: string) => void
): void => {
  updateHeadersFunction = fn;
};

// ============================================================================
// ApiService Class (Singleton Factory)
// ============================================================================

/**
 * Singleton factory for creating and managing Axios instances
 *
 * This class ensures that only one Axios instance exists per API key,
 * preventing unnecessary instance creation and ensuring consistent
 * configuration across the application.
 *
 * Features:
 * - Creates instances with proper configuration
 * - Handles withCredentials for specific instances (orchestrator)
 * - Automatically sets up interceptors on instance creation
 * - Provides cleanup methods for interceptor management
 *
 * @example
 * // Get or create an instance
 * const instance = ApiService.getInstance('orchestrator', { baseURL: 'https://api.example.com' });
 *
 * // Clear interceptors for reconfiguration
 * ApiService.clearInterceptors('orchestrator');
 */
export class ApiService {
  /**
   * Map storing all created Axios instances by their key
   * @private
   */
  private static instances: Map<string, AxiosInstance> = new Map();

  /**
   * Creates a new Axios instance with the provided configuration
   *
   * @param config - The API configuration object
   * @param key - Unique identifier for this instance
   * @returns The created Axios instance
   * @private
   */
  private static createInstance(config: ApiConfig, key: string): AxiosInstance {
    const baseURL = config.baseURL || '';

    if (!baseURL) {
      logger.warn(`[ApiService] Warning: Base URL is not defined for ${key}. Using empty string.`);
    }

    // Check if this instance should use credentials (cookies)
    const shouldUseCredentials = INSTANCES_WITH_CREDENTIALS.includes(
      key as (typeof INSTANCES_WITH_CREDENTIALS)[number]
    );

    const instance = axios.create({
      baseURL,
      withCredentials: shouldUseCredentials,
      headers: config.headers,
    });

    // Log for instances with credentials (debugging purposes)
    if (shouldUseCredentials) {
      logger.debug(`[ApiService] Instance ${key} created with withCredentials:`, {
        baseURL,
        withCredentials: true,
        timestamp: new Date().toISOString(),
      });
    }

    // Setup interceptors if updateHeadersFunction is available
    if (updateHeadersFunction) {
      setupInterceptors(instance, key, updateHeadersFunction);
    } else {
      logger.warn(`[ApiService] No updateHeadersFunction set for instance ${key}`);
    }

    return instance;
  }

  /**
   * Gets or creates an Axios instance for the given key
   *
   * If an instance already exists for the key, it is returned.
   * Otherwise, a new instance is created with the provided config.
   *
   * @param key - Unique identifier for the API instance
   * @param config - Configuration for creating a new instance (ignored if instance exists)
   * @returns The Axios instance for the given key
   *
   * @example
   * const orchestratorInstance = ApiService.getInstance('orchestrator', {
   *   baseURL: 'https://api.example.com/orchestrator'
   * });
   */
  public static getInstance(key: string, config: ApiConfig): AxiosInstance {
    if (!this.instances.has(key)) {
      this.instances.set(key, this.createInstance(config, key));
    }
    return this.instances.get(key)!;
  }

  /**
   * Clears interceptors for a specific instance
   *
   * Use this when you need to reconfigure interceptors or before
   * destroying an instance.
   *
   * @param key - The instance key to clear interceptors for
   *
   * @example
   * ApiService.clearInterceptors('orchestrator');
   */
  public static clearInterceptors(key: string): void {
    const instance = this.instances.get(key);
    if (instance) {
      clearInterceptors(instance, key);
    }
  }

  /**
   * Gets all instance keys currently registered
   *
   * Useful for debugging and testing purposes.
   *
   * @returns Array of all registered instance keys
   *
   * @example
   * const keys = ApiService.getInstanceKeys();
   * console.log('Registered instances:', keys);
   */
  public static getInstanceKeys(): string[] {
    return Array.from(this.instances.keys());
  }

  /**
   * Checks if an instance exists for the given key
   *
   * @param key - The instance key to check
   * @returns True if an instance exists for the key
   *
   * @example
   * if (ApiService.hasInstance('orchestrator')) {
   *   console.log('Orchestrator instance exists');
   * }
   */
  public static hasInstance(key: string): boolean {
    return this.instances.has(key);
  }
}

// ============================================================================
// ApiWrapper Class (HTTP Methods Wrapper)
// ============================================================================

/**
 * Wrapper class providing a clean interface to Axios instances
 *
 * This class implements the ApiMethods interface and provides:
 * - Lazy initialization (instance created on first HTTP call)
 * - All standard HTTP methods (GET, POST, PUT, PATCH, DELETE)
 * - Helper methods for setting authentication and organization headers
 * - Access to default headers for external configuration
 *
 * @implements {ApiMethods}
 *
 * @example
 * const api = new ApiWrapper('orchestrator', 'https://api.example.com');
 *
 * // Make HTTP requests
 * const response = await api.get('/users');
 * await api.post('/users', { name: 'John' });
 *
 * // Set authentication
 * api.setAuthToken('my-token');
 * api.setOrganizationUUID('org-uuid');
 */
export class ApiWrapper implements ApiMethods {
  /**
   * Unique identifier for this wrapper's Axios instance
   * @private
   */
  private key: string;

  /**
   * Base URL for the API
   * @private
   */
  private baseURL: string | undefined;

  /**
   * Creates a new ApiWrapper instance
   *
   * Note: The actual Axios instance is not created until the first HTTP call
   * (lazy initialization).
   *
   * @param key - Unique identifier for the underlying Axios instance
   * @param baseURL - Base URL for all requests made through this wrapper
   */
  constructor(key: string, baseURL: string | undefined) {
    this.key = key;
    this.baseURL = baseURL;
  }

  /**
   * Gets the underlying Axios instance
   *
   * Uses lazy initialization - creates the instance on first access.
   *
   * @returns The Axios instance for this wrapper
   *
   * @example
   * const axiosInstance = wrapper.getInstance();
   * axiosInstance.interceptors.request.use(customInterceptor);
   */
  public getInstance(): AxiosInstance {
    return ApiService.getInstance(this.key, { baseURL: this.baseURL });
  }

  /**
   * Provides access to default headers for external configuration
   *
   * This getter allows external code to read and modify the Authorization
   * header on the underlying Axios instance.
   *
   * @returns Object with headers.common containing Authorization accessor
   *
   * @example
   * // Read current authorization
   * const authHeader = wrapper.defaults.headers.common.Authorization;
   *
   * // Set authorization directly
   * wrapper.defaults.headers.common.Authorization = 'Bearer token';
   */
  public get defaults() {
    const instance = this.getInstance();
    return {
      headers: {
        common: {
          get Authorization(): AxiosHeaderValue | undefined {
            return instance.defaults.headers.common.Authorization;
          },
          set Authorization(value: AxiosHeaderValue | undefined) {
            instance.defaults.headers.common.Authorization = value;
          },
        },
      },
    };
  }

  // ==========================================================================
  // HTTP Methods
  // ==========================================================================

  /**
   * Performs a GET request
   *
   * @template T - The expected response data type
   * @param url - Request URL (relative to baseURL)
   * @param config - Optional Axios request configuration
   * @returns Promise resolving to AxiosResponse
   *
   * @example
   * const { data } = await api.get<User[]>('/users');
   * const { data } = await api.get<User>('/users/123', { params: { include: 'profile' } });
   */
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.getInstance().get<T>(url, config);
  }

  /**
   * Performs a POST request
   *
   * @template T - The expected response data type
   * @param url - Request URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional Axios request configuration
   * @returns Promise resolving to AxiosResponse
   *
   * @example
   * const { data } = await api.post<User>('/users', { name: 'John', email: 'john@example.com' });
   */
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.getInstance().post<T>(url, data, config);
  }

  /**
   * Performs a PUT request
   *
   * @template T - The expected response data type
   * @param url - Request URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional Axios request configuration
   * @returns Promise resolving to AxiosResponse
   *
   * @example
   * const { data } = await api.put<User>('/users/123', { name: 'John Updated' });
   */
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.getInstance().put<T>(url, data, config);
  }

  /**
   * Performs a PATCH request
   *
   * @template T - The expected response data type
   * @param url - Request URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional Axios request configuration
   * @returns Promise resolving to AxiosResponse
   *
   * @example
   * const { data } = await api.patch<User>('/users/123', { name: 'Partial Update' });
   */
  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.getInstance().patch<T>(url, data, config);
  }

  /**
   * Performs a DELETE request
   *
   * @template T - The expected response data type
   * @param url - Request URL (relative to baseURL)
   * @param config - Optional Axios request configuration
   * @returns Promise resolving to AxiosResponse
   *
   * @example
   * await api.delete('/users/123');
   * const { data } = await api.delete<DeleteResponse>('/users/123');
   */
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.getInstance().delete<T>(url, config);
  }

  // ==========================================================================
  // Helper Methods
  // ==========================================================================

  /**
   * Sets the authentication token for this API instance
   *
   * Updates the Authorization header with the provided token.
   * Supports both 'Token' and 'Bearer' authentication schemes.
   *
   * @param token - The authentication token (or undefined to clear)
   * @param type - The authentication type ('Token' or 'Bearer', defaults to 'Token')
   *
   * @example
   * // Set Bearer token (most common)
   * api.setAuthToken('my-jwt-token', 'Bearer');
   *
   * // Set Token authentication
   * api.setAuthToken('my-api-token', 'Token');
   *
   * // Clear authentication
   * api.setAuthToken(undefined);
   */
  setAuthToken(token: string | undefined, type: 'Token' | 'Bearer' = 'Token'): void {
    const authValue = token ? `${type} ${token}` : undefined;
    this.getInstance().defaults.headers.common.Authorization = authValue;
  }

  /**
   * Sets the organization UUID header for this API instance
   *
   * The X-Organization-UUID header is required by most API endpoints
   * for multi-tenant authorization.
   *
   * @param uuid - The organization UUID (or undefined to clear)
   *
   * @example
   * api.setOrganizationUUID('550e8400-e29b-41d4-a716-446655440000');
   *
   * // Clear organization
   * api.setOrganizationUUID(undefined);
   */
  setOrganizationUUID(uuid: string | undefined): void {
    this.getInstance().defaults.headers.common['X-Organization-UUID'] = uuid;
  }

  /**
   * Sets the Cognito User Pool header for this API instance
   *
   * The X-Cognito-UserPool header is used for AWS Cognito authentication.
   *
   * @param userPool - The Cognito User Pool ID (or undefined to clear)
   *
   * @example
   * api.setCognitoUserPool('us-east-1_XXXXXXXXX');
   */
  setCognitoUserPool(userPool: string | undefined): void {
    this.getInstance().defaults.headers.common['X-Cognito-UserPool'] = userPool;
  }

  /**
   * Gets the key identifier for this wrapper
   *
   * @returns The key string used to identify this API instance
   *
   * @example
   * const key = wrapper.getKey(); // 'orchestrator'
   */
  getKey(): string {
    return this.key;
  }

  /**
   * Gets the base URL for this wrapper
   *
   * @returns The base URL string or undefined
   *
   * @example
   * const baseUrl = wrapper.getBaseURL(); // 'https://api.example.com'
   */
  getBaseURL(): string | undefined {
    return this.baseURL;
  }
}
